import {
  getAccommodationV2ItemService,
  isAccommodationItem,
  isAccommodationV2Item,
  isCruiseItem,
  isExperienceItem,
  isFlightItem,
  isLEHotelItem,
  isTourV1Item,
  isTourV2Item,
} from 'lib/checkout/checkoutUtils'
import { createSelector } from 'reselect'
import { getExperienceTotals, getTransferTotals } from 'checkout/selectors/payment/experience'
import { getAccommodationTotals } from 'checkout/selectors/payment/accommodation'
import { getVirginFlightTotals } from 'checkout/selectors/payment/flights'
import { computeFeesTotal } from 'checkout/lib/utils/flights/fees'
import { VIRGIN_VELOCITY_CATALOGUE_REFERENCE_MAP, VIRGIN_VELOCITY_BURN_MINIMUM_CARD_PAYMENT } from 'constants/partnerships'
import { getFlightItems, getFlightItemsView } from '../view/flights'
import { excludeNullOrUndefined } from 'checkout/utils'
import { VIRGIN_AUSTRALIA } from 'constants/flight'
import {
  CHECKOUT_ITEM_TYPE_BEDBANK,
  CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION,
  CHECKOUT_ITEM_TYPE_CAR_HIRE,
  CHECKOUT_ITEM_TYPE_FLIGHT,
  CHECKOUT_ITEM_TYPE_INSURANCE,
  CHECKOUT_ITEM_TYPE_TRANSFER,
} from 'constants/checkout'
import { PRODUCT_TYPE_ULTRALUX } from 'constants/offer'
import { PAYMENT_OPTIONS } from 'constants/payment'
import getCheckoutTotalsView from 'checkout/selectors/payment/getCheckoutTotalsView'
import { isUsingCredit } from './paymentUsageSelectors'

const isUltraLuxItem = (offer: App.Offer): boolean => {
  return offer?.productType === PRODUCT_TYPE_ULTRALUX
}

const isV2BedbankItem = (
  item: App.Checkout.AnyItem,
  accommodationSessions: App.CheckoutState['accommodationSessions'],
): boolean => isAccommodationV2Item(item) && getAccommodationV2ItemService(item, accommodationSessions) === 'bedbank'

// Eligible v2 items are reservation stays: bedbank ones are filtered out by getVelocityEligibleItems
const isLEHotelOrV2Item = (item: App.Checkout.AnyItem): boolean =>
  isLEHotelItem(item) || isAccommodationV2Item(item)

export const isVelocityCardValid = createSelector(
  (state: App.State) => state.checkout.payment.sso.velocity.isAuthenticated,
  (state: App.State) => state.checkout.payment.partnerships?.velocity?.burn,
  (state: App.State) => state.checkout.payment.paymentSelected,
  (state: App.State) => isUsingCredit(state),
  (isAuthenticated, velocityBurnState, paymentSelection, isUsingCredit): boolean => {
    return isAuthenticated &&
      !!velocityBurnState &&
      !!velocityBurnState.pointsBurned &&
      velocityBurnState.pointsBurned > 0 &&
      paymentSelection === PAYMENT_OPTIONS.FULL &&
      !isUsingCredit
  },
)

export const getVelocityBurnableAmount = createSelector(
  (state: App.State) => state.checkout?.promo?.discountTotal ?? 0,
  (state: App.State) => isUsingCredit(state) ? getCheckoutTotalsView(state).data.amountPayableByCredit : 0,
  (state: App.State) => getAccommodationTotals(state),
  (state: App.State) => getExperienceTotals(state),
  (state: App.State) => getTransferTotals(state),
  (state: App.State) => getVirginFlightTotals(state),
  (promoDiscountTotal, creditTotal, ...totalViews): App.WithDataStatus<number> => {
    return totalViews.reduce<App.WithDataStatus<number>>((acc, view) => {
      const otherFees = view.data.otherFees ?
        typeof view.data.otherFees === 'number' ? view.data.otherFees : computeFeesTotal(view.data.otherFees) :
        0
      return {
        hasRequiredData: acc.hasRequiredData && view.hasRequiredData,
        data: acc.data +
          (view.data?.price ?? 0) +
          (view.data?.surcharge ?? 0) +
          otherFees,
      }
    }, { hasRequiredData: true, data: -promoDiscountTotal - creditTotal - VIRGIN_VELOCITY_BURN_MINIMUM_CARD_PAYMENT })
  },
)

const getVirginFlightItems = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getFlightItemsView(state),
  (flightItems, flightItemsView) => {
    const virginFlightItemsView = flightItemsView.data
      .filter(excludeNullOrUndefined)
      .filter(view => view.validatingCarrierName === VIRGIN_AUSTRALIA)
    return flightItems.filter((item) => virginFlightItemsView.some(view => view.journeyId === item.searchId))
  },
)

export const getVelocityEligibleItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getVirginFlightItems(state),
  (state: App.State) => state.checkout.accommodationSessions,
  (items, virginFlights, accommodationSessions): Array<App.Checkout.AnyItem> => {
    const eligibleItems = items.filter((item) => {
      return ![
        CHECKOUT_ITEM_TYPE_INSURANCE,
        CHECKOUT_ITEM_TYPE_BEDBANK,
        CHECKOUT_ITEM_TYPE_CAR_HIRE,
        CHECKOUT_ITEM_TYPE_FLIGHT,
        CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION,
        CHECKOUT_ITEM_TYPE_TRANSFER,
      ].includes(item.itemType) && !isV2BedbankItem(item, accommodationSessions)
    })
    return eligibleItems.concat(virginFlights)
  },
)

export const mapOrderItemTypesToVelocityCatalogueRef = createSelector(
  (state: App.State) => getVelocityEligibleItems(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.properties,
  (items, offers, properties): VIRGIN_VELOCITY_CATALOGUE_REFERENCE_MAP | null => {
    if (items.length > 1) {
      // multi-room booking should still be hotel
      if (items.every(isLEHotelOrV2Item)) {
        return VIRGIN_VELOCITY_CATALOGUE_REFERENCE_MAP.HOTEL
      }
      return VIRGIN_VELOCITY_CATALOGUE_REFERENCE_MAP.PACKAGE
    } else if (items.length === 1) {
      const item = items[0]
      if (isFlightItem(item)) {
        return VIRGIN_VELOCITY_CATALOGUE_REFERENCE_MAP.FLIGHT
      }
      if (isExperienceItem(item)) {
        return VIRGIN_VELOCITY_CATALOGUE_REFERENCE_MAP.EXPERIENCE
      }
      if (isAccommodationItem(item) && offers && isUltraLuxItem(offers[item.offerId])) {
        return VIRGIN_VELOCITY_CATALOGUE_REFERENCE_MAP.ULTRA_LUX
      }
      if (isAccommodationV2Item(item) && properties[item.propertyId]?.property?.isUltraLux) {
        return VIRGIN_VELOCITY_CATALOGUE_REFERENCE_MAP.ULTRA_LUX
      }
      if (isTourV1Item(item) || isTourV2Item(item)) {
        return VIRGIN_VELOCITY_CATALOGUE_REFERENCE_MAP.TOUR
      }
      if (isCruiseItem(item)) {
        return VIRGIN_VELOCITY_CATALOGUE_REFERENCE_MAP.CRUISE
      }
      if (isLEHotelOrV2Item(item)) {
        return VIRGIN_VELOCITY_CATALOGUE_REFERENCE_MAP.HOTEL
      }
    }
    return null
  },
)
